import { useState, useEffect } from 'react';
import { usePaywall } from '@/hooks/usePaywall';
import { formatTimestamp } from '@/utils/formatTimestamp';
import { trackCustomEvent } from '@/lib/appInsights';

import { RxCross1 } from 'react-icons/rx';
import Button from '@/components/elements/Button';
import Modal from '@/components/elements/Modal';
import Toggle from '@/components/elements/Toggle';
import Notification from '@/components/elements/Notification';

import {
  CancelSubscriptionModalText,
  SubscriptionCardButtonText,
  SubscriptionSettingsText,
  SubscriptionStatus,
  TrackingCustomEvents,
  TrialStatus,
} from '@/constants/constants';

import type { Subscription } from '@/types/models.types';
import type { PaywallContextType } from '@/types/util.types';
import type { SubscriptionSettings as SettingsType } from '@/types/models.types';

interface SubscriptionSettingsProps {
  subscription: Subscription;
  authorName: string;
  onClose: () => void;
}

const SubscriptionSettings = ({ subscription, authorName, onClose }: SubscriptionSettingsProps) => {
  const { InTrial } = TrialStatus;
  const { Expired, Cancelling, Paid, Trialing } = SubscriptionStatus;
  const { getSubscriptionSettings, setSubscriptionSettings, cancelSubscription } = usePaywall() as PaywallContextType;
  const [hasError, setHasError] = useState<boolean>(false);
  const [settings, setSettings] = useState<SettingsType | null>(null);
  const [showConfirmation, setShowConfirmation] = useState<boolean>(false);
  const [settingsIsLoading, setSettingsIsLoading] = useState<boolean>(true);
  const [subscriptionIsLoading, setSubscriptionIsLoading] = useState<boolean>(false);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const settings = await getSubscriptionSettings(subscription.id);
        setSettings(settings);
      } finally {
        setSettingsIsLoading(false);
      }
    };

    fetchSettings();
  }, [subscription.id, getSubscriptionSettings]);

  const renderErrorNotification = () =>
    hasError && (
      <div className='max-w-lg mx-auto'>
        <Notification type='error' message='An error occurred whilst cancelling the subscription' />
      </div>
    );

  const handleCancelSubscription = async () => {
    setSubscriptionIsLoading(true);
    setShowConfirmation(false);

    try {
      const result = await cancelSubscription(subscription.stripeSubscriptionId);
      const eventType =
        subscription.trialStatus === InTrial
          ? TrackingCustomEvents.click.purchases.cancel
          : TrackingCustomEvents.click.purchases.unsubscribe;

      trackCustomEvent(eventType, {
        subscription_id: subscription.stripeSubscriptionId,
        author_id: subscription.author.id,
        subscription_status: subscription.status,
      });

      if (result && result.success) {
        setSubscriptionIsLoading(false);
        onClose();
        return;
      }
    } catch {
      setHasError(true);
    } finally {
      setSubscriptionIsLoading(false);
    }
  };

  const handleSettingsChange = async (checked: boolean) => {
    if (!settings) return;

    setSettingsIsLoading(true);

    const updatedSettings = await setSubscriptionSettings(subscription.id, {
      ...settings,
      emailNotificationsEnabled: checked,
    });

    setSettings(updatedSettings);
    setSettingsIsLoading(false);
  };

  const renderActionButton = () => {
    const isDisabled = subscription.status === Cancelling || subscription.status === Expired || subscriptionIsLoading;

    const getButtonStyle = () => {
      switch (subscription.status) {
        case Paid:
          return 'gradient';
        case Cancelling:
          return 'hollow';
        case Expired:
          return 'disabled';
        case Trialing:
          return 'faded';
        default:
          return 'gradient';
      }
    };

    const getButtonText = () => {
      switch (subscription.status) {
        case Paid:
          return SubscriptionCardButtonText.Paid;
        case Cancelling:
          return SubscriptionCardButtonText.Cancelling;
        case Expired:
          return SubscriptionCardButtonText.Expired;
        case Trialing:
          return SubscriptionCardButtonText.Trialing;
        default:
          return SubscriptionCardButtonText.Paid;
      }
    };

    return (
      <Button
        data-cy='subscription-card-button'
        variant={getButtonStyle()}
        disabled={isDisabled}
        isLoading={subscriptionIsLoading}
        buttonText={getButtonText()}
        onClick={() => {
          if (subscription.status === Paid || subscription.status === Trialing) {
            setShowConfirmation(true);
          }
        }}
      />
    );
  };

  return (
    <div className='flex flex-col h-full items-left relative -mt-1'>
      <button
        onClick={onClose}
        className='absolute flex items-center justify-center w-[34px] h-[34px] -right-3 -top-[5px] p-2 rounded-md hover:bg-gray-100 transition-colors'
        aria-label='Close dialog'
      >
        <RxCross1 className='w-5 h-5' />
      </button>
      <h3 className='text-lg text-left'>{SubscriptionSettingsText.EmailNotifications}</h3>
      <p className='mb-4'>
        Get notified by email when <strong>{authorName}</strong> publishes new content.
      </p>
      <div className='flex flex-col mb-4 w-full'>
        <Toggle
          disabled={settingsIsLoading}
          onChange={handleSettingsChange}
          checked={settings?.emailNotificationsEnabled || false}
          label={settings?.emailNotificationsEnabled ? 'Notifications Enabled' : 'Notifications Disabled'}
        />
      </div>
      {(subscription.status === Paid || subscription.status === Trialing) && (
        <>
          <h3 className='text-lg text-left mt-3'>{SubscriptionSettingsText.ManageSubscription}</h3>
          {renderErrorNotification()}
          {renderActionButton()}

          <Modal
            title='Cancel Subscription'
            isOpen={showConfirmation}
            onClose={() => setShowConfirmation(false)}
            buttons={[
              <Button
                key='close-modal'
                variant='hollow'
                buttonText='Back'
                disabled={subscriptionIsLoading}
                onClick={() => setShowConfirmation(false)}
              />,
              <Button
                key='cancel-subscription'
                data-cy='confirm-cancel'
                variant='gradient'
                buttonText={subscriptionIsLoading ? 'Unsubscribing' : 'Confirm'}
                disabled={subscriptionIsLoading}
                onClick={handleCancelSubscription}
              />,
            ]}
          >
            <div className='text-gray-900'>
              <p>
                {CancelSubscriptionModalText.Confirmation} <strong>{authorName}</strong>?
              </p>

              <p className='mt-3'>
                {CancelSubscriptionModalText.AccessRetained} {formatTimestamp(subscription.periodEnd)}.
              </p>
            </div>
          </Modal>
        </>
      )}
    </div>
  );
};

export default SubscriptionSettings;
